import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiFlagFill, PiPaperclipLight, PiUserLight, PiChatCircleTextLight } from 'react-icons/pi';
import { Task } from './types';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { formatDate, formatStatus } from '../../utils/functions';
import ProfileImage from '../Header/ProfileImage';
import { getInitials } from '../shared/TailwindComponents/CollaboratorsBadge';

interface TaskGridItemProps {
  task: Task;
  onClick: () => void;
  currentUserId: string;
}

const TaskGridItem: React.FC<TaskGridItemProps> = ({ task, onClick, currentUserId }) => {
  const { t } = useTranslation();

  const getStatusColor = (status?: string): BadgeColor => {
    if (!status) return 'gray';
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === 'todo' || lowerStatus === 'active') return 'blue';
    if (lowerStatus === 'in_progress') return 'blue';
    if (lowerStatus === 'on_hold') return 'yellow';
    if (lowerStatus === 'completed') return 'green';
    if (lowerStatus === 'archived' || lowerStatus === 'deleted') return 'red';
    return 'gray';
  };

  const getPriorityColor = (priority?: string): string => {
    const lowerPriority = priority?.toLowerCase() || '';
    if (lowerPriority === 'critical' || lowerPriority === 'high') return 'red';
    if (lowerPriority === 'medium') return 'yellow';
    if (lowerPriority === 'low') return 'green';
    return 'gray';
  };

  // Get collaborators from new API structure or fallback to old structure
  const collaborators = task.collaborators || task.Collaborators || [];

  // Get counts from new API structure or fallback to old structure
  const attachmentCount = typeof task.attachment_count === 'number' ? task.attachment_count : (task.Attachments ? task.Attachments.length : 0);
  const commentCount = typeof task.comment_count === 'number' ? task.comment_count : (task.Comments ? task.Comments.length : 0);

  const dateRange = `${formatDate(task.CreationDate)} - ${task.TaskDeadline !== "" ? formatDate(task.TaskDeadline) : 'Deadline not set'}`;

  return (
    <div
      onClick={onClick}
      className="relative cursor-pointer bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-md text-gray-900 dark:text-white truncate">{task.Title}</h3>
        <Badge color={getStatusColor(task.Status)}>{formatStatus(t((task.Status || 'Active').toLowerCase()))}</Badge>
      </div>

      <div className="text-sm text-gray-500 dark:text-gray-400">{dateRange}</div>

      <div className="flex items-center gap-2">
        <PiFlagFill className={`w-4 h-4 text-${getPriorityColor(task.Priority)}-500`} />
        <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{task.Priority ? t(task.Priority.toLowerCase()) : t('None')}</span>
      </div>

      <div className='flex justify-between items-center gap-2'>
        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <PiPaperclipLight className="w-4 h-4" />
            <span>{attachmentCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <PiChatCircleTextLight className="w-4 h-4" />
            <span>{commentCount}</span>
          </div>
        </div>

        {collaborators.length > 0 && (
          <div className="flex -space-x-2">
            {collaborators.slice(0, 3).map((collab: any, index: number) => {
              const firstName = collab.first_name || collab.FirstName || '';
              const lastName = collab.last_name || collab.LastName || '';
              const fullName = collab.name || `${firstName} ${lastName}`.trim();
              const nameParts = fullName.trim().split(/\s+/);
              const email = collab.email || collab.Email || '';
              const initials = collab.initials || `${nameParts[0]?.[0] || ''}${nameParts[1]?.[0] || ''}`.toUpperCase() || 'U';

              return (
                <div key={index} className="relative" title={fullName}>
                  <ProfileImage
                    profilePicture={collab.profile_picture || collab.avatar_url || null}
                    email={email}
                    initials={initials}
                    size="w-8 h-8"
                    firstName={firstName}
                    lastName={lastName}
                  />
                </div>
              );
            })}
            {collaborators.length > 3 && (
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-700 dark:text-gray-200">
                +{collaborators.length - 3}
              </div>
            )}
          </div>
        )}
      </div>

      {task.AssignedBy !== currentUserId && (
        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
          <PiUserLight className="w-3 h-3" />
          <span>{t('Assigned by')} {task.AssignedByName}</span>
        </div>
      )}
    </div>
  );
};

export default TaskGridItem;
